import { NextRequest, NextResponse } from "next/server";
import { sendMail } from "@/lib/mailer";
import { buildEmailHtml } from "@/lib/emailTemplate";
import { FORM_CONFIGS } from "@/lib/formConfigs";

export async function POST(
    req: NextRequest,
    { params }: { params: Promise<{ formId: string }> }
) {
    const { formId } = await params;
    const config = FORM_CONFIGS[formId];

    if (!config) {
        return NextResponse.json({ error: "Unknown form." }, { status: 404 });
    }

    try {
        const contentType = req.headers.get("content-type") || "";
        const isMultipart = contentType.includes("multipart/form-data");

        // Normalize both JSON and FormData into a single map
        let dataMap: Record<string, string> = {};
        let file: File | null = null;

        if (isMultipart) {
            const formData = await req.formData();
            for (const field of config.fields) {
                dataMap[field.key] = formData.get(field.key)?.toString() ?? "";
            }
            if (config.fileField) {
                file = formData.get(config.fileField) as File | null;
            }
        } else {
            const json = await req.json();
            for (const field of config.fields) {
                dataMap[field.key] = json[field.key]?.toString() ?? "";
            }
        }

        // Validate required fields
        for (const field of config.fields) {
            if (field.required && !dataMap[field.key]) {
                return NextResponse.json(
                    { error: `Missing required field: ${field.label}` },
                    { status: 400 }
                );
            }
        }

        const emailFields = config.fields.map((f) => ({
            label: f.label,
            value: dataMap[f.key],
        }));

        let attachments;
        if (file && file.size > 0) {
            const buffer = Buffer.from(await file.arrayBuffer());
            attachments = [{ filename: file.name, content: buffer, contentType: file.type }];
        }

        const emailFieldConfig = config.fields.find((f) =>
            f.key.toLowerCase().includes("email")
        );
        const replyTo = emailFieldConfig ? dataMap[emailFieldConfig.key] : undefined;

        await sendMail({
            to: config.toEmail,
            subject: config.title,
            html: buildEmailHtml(config.title, emailFields),
            replyTo,
            attachments,
        });

        return NextResponse.json({ success: true });
    } catch (err) {
        console.error(`Form [${formId}] error:`, err);
        return NextResponse.json({ error: "Failed to send email." }, { status: 500 });
    }
}